/**
 * @fileoverview Grouped catalog mapping panel for the import-run and template-authoring wizards.
 * @module import-editor/components
 *
 * Responsibilities:
 * - Render a scrollable grouped table of target fields with source-column pickers,
 *   transform selectors, default-value inputs, match-key toggles, and inline previews.
 * - Delegate stats, AI-suggest, sample-data, and ignored-column tooling to
 *   MappingSummaryPanel (rendered above this table by the parent or internally).
 * - Support custom (non-catalog) target fields via AddCustomTargetRow.
 *
 * Dependencies:
 * - React (useState, useMemo, useCallback, Fragment)
 * - lucide-react icons
 * - @firesquirrel/local-db types (FieldMapping, FieldTransformType, ImportDataMode)
 * - getTargetFieldGroups / getTargetFieldOptions / resolveTargetFieldValue
 * - MappingSuggestion type from ./FieldMappingsTabContent
 * - MappingSummaryPanel for the stats + AI-suggest + tabbed-detail panel
 * - SourceColumnCombobox for the searchable column picker
 *
 * Architecture:
 * - No API calls, no Dexie reads/writes — all state is lifted to the parent.
 * - AI-suggestion state is local (suggestions, dismissed) and written back via
 *   onMappingsChange when accepted.
 *
 * Notes:
 * - The stats header, AI suggestions list, and ignored-columns section previously
 *   inline in this component have been moved into MappingSummaryPanel tabs.
 */

import { useState, useMemo, useCallback, useEffect, Fragment } from 'react';
import { X, ChevronDown, ChevronRight, Key, Plus, Trash2, Eye, Type, RotateCcw } from 'lucide-react';
import type { FieldMapping, FieldTransform, FieldTransformType, ImportDataMode } from '@firesquirrel/local-db';
import { getTargetFieldGroups, getTargetFieldOptions, resolveTargetFieldValue, type TargetFieldOption } from '../utils/targetFieldOptions';
import { getSuggestionMappingSource, type MappingSuggestion } from './FieldMappingsTabContent';
import { MappingSummaryPanel } from './MappingSummaryPanel';
import { SourceColumnCombobox } from './SourceColumnCombobox';
import type { GroupInterpretation } from '../unifiedWizard/services';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RunWizardColumnMappingPanelProps {
  /** Current mappings (template-seeded, user-edited) */
  fieldMappings: FieldMapping[];
  /** Called whenever mappings change */
  onMappingsChange: (next: FieldMapping[]) => void;
  /** Data mode of the selected template — drives catalog */
  dataMode: ImportDataMode;
  /** Columns parsed from the data file (dropdown options) */
  availableColumns: string[];
  /** Sample rows — enables inline preview + AI suggest quality */
  sampleRows: Record<string, unknown>[];
  /** Injected by the app — fetch AI/rule mapping suggestions */
  suggestMappings?: (params: {
    dataMode: string;
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    tier?: string;
  }) => Promise<{ suggestions: MappingSuggestion[] }>;
  /** Injected by the app — fetch AI column group interpretations (Phase 5.4) */
  interpretColumnGroups?: (params: {
    dataMode: string;
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    groups?: Array<{ columns: string[]; groupHint?: string }>;
    autoDetect?: boolean;
  }) => Promise<{
    interpretations: GroupInterpretation[];
    used: boolean;
    error?: string;
  }>;
  /** Optional: lock this panel while the run is being committed */
  disabled?: boolean;
  /** Optional validation errors for the current mappings step. */
  errors?: string[];
}

// Transforms applied synchronously by the import-core transform engine
// during sample, dry-run, and commit. Every non-AI FieldTransformType is
// implemented server-side — see applyTransforms.ts.
const TRANSFORMS_WORKING: FieldTransformType[] = [
  'none', 'trim', 'uppercase', 'lowercase', 'number', 'date', 'sanitize',
  'concat', 'coalesce', 'split', 'prefix', 'suffix', 'lookup',
];
// AI-powered transforms — resolved server-side via the model-registry
// `import-row-parser` task; batched and previewed during dry run.
// aiClassify is intentionally absent (legacy templates that already carry it
// still render, but no new picker entry is offered).
const TRANSFORMS_AI: FieldTransformType[] = ['aiParse', 'aiSplit'];

export interface ConcatSeparatorOption {
  value: string;
  label: string;
}

export const CONCAT_SEPARATOR_OPTIONS: ConcatSeparatorOption[] = [
  { value: ' ', label: 'Space' },
  { value: ', ', label: 'Comma + space' },
  { value: '-', label: 'Hyphen' },
  { value: ' - ', label: 'Space hyphen space' },
  { value: '/', label: 'Slash' },
  { value: ' | ', label: 'Space pipe space' },
];

export function getConcatSeparatorOptions(currentSeparator?: string): ConcatSeparatorOption[] {
  const supported = CONCAT_SEPARATOR_OPTIONS.some((option) => option.value === currentSeparator);
  if (!currentSeparator || supported) {
    return CONCAT_SEPARATOR_OPTIONS;
  }

  return [
    { value: currentSeparator, label: `Legacy custom (${JSON.stringify(currentSeparator)})` },
    ...CONCAT_SEPARATOR_OPTIONS,
  ];
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function sampleValuesFor(rows: Record<string, unknown>[], col: string, max = 3): string[] {
  if (!col || !rows.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    const v = r?.[col];
    if (v === null || v === undefined || v === '') continue;
    const s = String(v);
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= max) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function RunWizardColumnMappingPanel({
  fieldMappings,
  onMappingsChange,
  dataMode,
  availableColumns,
  sampleRows,
  suggestMappings,
  interpretColumnGroups,
  disabled = false,
  errors = [],
}: RunWizardColumnMappingPanelProps) {
  // ---- Catalog (grouped) ----
  const catalog = useMemo(() => getTargetFieldOptions(dataMode), [dataMode]);
  const groups = useMemo(() => getTargetFieldGroups(dataMode), [dataMode]);
  const catalogValues = useMemo(() => new Set(catalog.map(o => o.value)), [catalog]);

  // ---- Map existing mappings by targetField for O(1) lookup ----
  const mappingByTarget = useMemo(() => {
    const m = new Map<string, { mapping: FieldMapping; index: number }>();
    fieldMappings.forEach((fm, index) => m.set(fm.targetField, { mapping: fm, index }));
    return m;
  }, [fieldMappings]);

  // ---- Extra (non-catalog) custom mappings already in the template ----
  const extraMappings = useMemo(
    () => fieldMappings.filter(fm => !catalogValues.has(fm.targetField)),
    [fieldMappings, catalogValues],
  );

  // ---- Group collapse state ----
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (g: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  };

  // ---- Inline preview state (which source column is being previewed per row) ----
  const [previewRow, setPreviewRow] = useState<string | null>(null);

  // ---- Per-row "default value mode" — when enabled, the Transform cell shows
  // a default-value input instead of the transform dropdown.
  const [defaultValueMode, setDefaultValueMode] = useState<Set<string>>(new Set());
  const toggleDefaultMode = (targetField: string) => {
    setDefaultValueMode(prev => {
      const next = new Set(prev);
      if (next.has(targetField)) next.delete(targetField);
      else next.add(targetField);
      return next;
    });
  };

  // ---- AI suggest state ----
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // ---- Column group interpretation state (Phase 5.4) ----
  const [groupInterpretations, setGroupInterpretations] = useState<GroupInterpretation[]>([]);
  const [groupInterpretLoading, setGroupInterpretLoading] = useState(false);
  const [groupInterpretError, setGroupInterpretError] = useState<string | null>(null);

  // Reset AI suggestions and group interpretations when the parsed file's
  // columns change (e.g. re-parsing a different sheet or file). Stale
  // suggestions/interpretations referencing old columns would be misleading.
  useEffect(() => {
    setSuggestions([]);
    setSuggestError(null);
    setDismissed(new Set());
    setGroupInterpretations([]);
    setGroupInterpretError(null);
  }, [availableColumns]);

  const mappedSourceColumns = useMemo(
    () => new Set(fieldMappings.flatMap(m => m.sourceColumns).filter(Boolean)),
    [fieldMappings],
  );
  const mappedTargetFields = useMemo(
    () => new Set(fieldMappings.filter(m => m.sourceColumns?.[0]).map(m => m.targetField)),
    [fieldMappings],
  );

  const suggestionSourceCols = (s: MappingSuggestion): string[] =>
    s.sourceColumns ?? [s.sourceColumn];

  const pendingSuggestions = suggestions.filter(
    s => suggestionSourceCols(s).every(col => !dismissed.has(col))
      && suggestionSourceCols(s).every(col => !mappedSourceColumns.has(col))
      && !mappedTargetFields.has(s.targetField),
  );

  const handleAutoSuggest = useCallback(async () => {
    if (!suggestMappings || availableColumns.length === 0) return;
    setSuggestLoading(true);
    setSuggestError(null);
    try {
      const result = await suggestMappings({
        dataMode,
        columns: availableColumns,
        sampleRows,
        tier: 'both',
      });
      setSuggestions(result.suggestions);
      setDismissed(new Set());
    } catch (err: any) {
      setSuggestError(err?.message ?? 'Suggest failed');
    } finally {
      setSuggestLoading(false);
    }
  }, [availableColumns, sampleRows, dataMode, suggestMappings]);

  /** Dismiss a single AI suggestion without mapping it. */
  const handleDismissSuggestion = useCallback((s: MappingSuggestion) => {
    setDismissed(prev => new Set([...prev, ...suggestionSourceCols(s)]));
  }, []);

  // ---- Column group interpretation handlers (Phase 5.4) ----
  const handleInterpretGroups = useCallback(async () => {
    if (!interpretColumnGroups || availableColumns.length === 0) return;
    setGroupInterpretLoading(true);
    setGroupInterpretError(null);
    try {
      const result = await interpretColumnGroups({
        dataMode,
        columns: availableColumns,
        sampleRows,
        autoDetect: true,
      });
      if (result.error) {
        setGroupInterpretError(result.error);
      }
      setGroupInterpretations(result.interpretations);
    } catch (err: any) {
      setGroupInterpretError(err?.message ?? 'Group interpretation failed');
    } finally {
      setGroupInterpretLoading(false);
    }
  }, [availableColumns, sampleRows, dataMode, interpretColumnGroups]);

  /** Accept all mappings from a group interpretation. */
  const handleAcceptGroupInterpretation = useCallback((group: GroupInterpretation) => {
    // Build a lookup of suggested transforms by target field.
    // Target fields from the AI are resolved through resolveTargetFieldValue
    // to normalize aliases/case to canonical catalog values, matching the
    // single/bulk suggestion acceptors. Unresolvable transform targets are
    // skipped (the mapping itself is also skipped below).
    // NOTE: A suggested transform is only applied if group.mappings
    // contains an entry with a matching (resolved) targetField. If the AI
    // returns a transform whose targetField is not in mappings (e.g.
    // combining addr1+addr2 into a new fullAddress field), that transform
    // is silently dropped. The AI contract routes multi-column→one-field
    // cases through suggestedTransforms with a matching mapping entry,
    // so this is expected to be the common case.
    const transformByTarget = new Map<string, FieldTransform>();
    if (group.suggestedTransforms) {
      for (const t of group.suggestedTransforms) {
        const resolvedTarget = resolveTargetFieldValue(t.targetField, dataMode);
        if (!resolvedTarget) continue;
        // Translate backend transform vocabulary to FieldTransformType.
        // NOTE: The backend suggestedTransforms shape does not carry
        // separator, outputFields, categories, or other AI-transform
        // config. AI transforms (aiSplit/aiParse/aiClassify) are applied
        // with only { type, instruction? } — the transform executor
        // tolerates this and uses defaults for the missing fields. Users
        // may need to manually configure outputFields/categories after
        // accepting if the AI transform requires them.
        let transformType: FieldTransformType = 'none';
        let transformExtra: Partial<FieldTransform> = {};
        switch (t.type) {
          case 'concatenate':
            transformType = 'concat';
            // NOTE: separator is hardcoded to space because the backend
            // suggestedTransforms type does not carry a separator. If the
            // AI intends a comma/hyphen join, the user must adjust manually.
            transformExtra = { separator: ' ' };
            break;
          case 'split':
            transformType = 'aiSplit';
            break;
          case 'parse':
            transformType = 'aiParse';
            break;
          case 'classify':
            transformType = 'aiClassify';
            break;
        }
        if (t.instruction) transformExtra.instruction = t.instruction;
        transformByTarget.set(resolvedTarget, { type: transformType, ...transformExtra });
      }
    }

    // Apply each mapping from the group.
    // If two mappings share the same targetField (unlikely per the AI
    // contract, which routes multi-column→one-field through
    // suggestedTransforms), accumulate source columns instead of
    // overwriting the earlier entry.
    const newMappings = [...fieldMappings];
    // Track targets written during THIS loop so we only accumulate source
    // columns for genuine duplicates within the group, not for pre-existing
    // template/user mappings (which should be replaced, matching the
    // acceptSuggestion / acceptAllHigh semantics).
    const targetsWrittenThisLoop = new Set<string>();
    for (const m of group.mappings) {
      // Resolve the AI-suggested target field to a canonical catalog value.
      // Skip unresolvable entries so hallucinated/unknown fields don't
      // create "Custom / Other" mappings that silently drop data.
      const resolvedTarget = resolveTargetFieldValue(m.targetField, dataMode);
      if (!resolvedTarget) continue;

      const existingIdx = newMappings.findIndex(fm => fm.targetField === resolvedTarget);
      const suggestedTransform = transformByTarget.get(resolvedTarget);
      // Determine source columns: if a concat transform is suggested, use all source columns
      const concatTransform = group.suggestedTransforms?.find(
        t => t.type === 'concatenate' && resolveTargetFieldValue(t.targetField, dataMode) === resolvedTarget
      );
      const sourceColumns = concatTransform ? concatTransform.sourceColumns : [m.sourceColumn];
      const newMapping: FieldMapping = {
        targetField: resolvedTarget,
        sourceColumns,
        isMatchKey: false,
        transform: suggestedTransform ?? { type: 'none' },
        confidenceScore: m.confidence,
        mappingSource: 'ai',
      };
      if (existingIdx >= 0) {
        const existing = newMappings[existingIdx];
        // Only accumulate source columns when this target was already
        // written earlier in THIS loop (genuine duplicate within the
        // group). For pre-existing template/user mappings, replace
        // sourceColumns outright — matching acceptSuggestion semantics.
        const isDuplicateWithinGroup =
          !concatTransform && targetsWrittenThisLoop.has(resolvedTarget);
        const mergedSourceColumns = isDuplicateWithinGroup
          ? Array.from(new Set([...(existing.sourceColumns ?? []), ...sourceColumns]))
          : sourceColumns;
        newMappings[existingIdx] = {
          ...existing,
          targetField: resolvedTarget,
          sourceColumns: mergedSourceColumns,
          confidenceScore: m.confidence,
          mappingSource: 'ai',
          // Only override transform if the interpretation suggested one
          transform: suggestedTransform ?? existing.transform,
          // Preserve existing isMatchKey — don't reset user's match-key config
        };
      } else {
        newMappings.push(newMapping);
      }
      targetsWrittenThisLoop.add(resolvedTarget);
    }
    onMappingsChange(newMappings);
    // Remove this group from the list
    setGroupInterpretations(prev => prev.filter(g => g !== group));
  }, [fieldMappings, onMappingsChange, dataMode]);

  /** Dismiss a group interpretation without applying it. */
  const handleDismissGroupInterpretation = useCallback((group: GroupInterpretation) => {
    setGroupInterpretations(prev => prev.filter(g => g !== group));
  }, []);

  // ---- Mutation helpers ----
  const upsertMapping = (targetField: string, patch: Partial<FieldMapping>) => {
    const existing = mappingByTarget.get(targetField);
    if (existing) {
      const next = [...fieldMappings];
      next[existing.index] = { ...existing.mapping, ...patch, targetField };
      onMappingsChange(next);
    } else {
      const base: FieldMapping = {
        targetField,
        sourceColumns: [''],
        isMatchKey: false,
        transform: { type: 'none' },
      };
      onMappingsChange([...fieldMappings, { ...base, ...patch, targetField }]);
    }
  };

  const setSourceColumn = (targetField: string, sourceCol: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    // Preserve additional columns when transform is multi-source (concat/coalesce)
    if ((existing?.transform?.type === 'concat' || existing?.transform?.type === 'coalesce')
      && existing.sourceColumns && existing.sourceColumns.length > 1) {
      const rest = existing.sourceColumns.slice(1);
      upsertMapping(targetField, { sourceColumns: [sourceCol || '', ...rest] });
    } else {
      upsertMapping(targetField, { sourceColumns: sourceCol ? [sourceCol] : [''] });
    }
  };

  const setTransform = (targetField: string, type: FieldTransformType) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    const isMultiSource = type === 'concat' || type === 'coalesce';
    const wasMultiSource =
      existing?.transform?.type === 'concat' || existing?.transform?.type === 'coalesce';
    // Seed a 2-column array when switching TO a multi-source transform so the
    // UI immediately shows a second column picker. Keep as-is if it's already
    // multi-column.
    if (isMultiSource && existing && (existing.sourceColumns?.length ?? 0) < 2) {
      const first = existing.sourceColumns?.[0] ?? '';
      upsertMapping(targetField, {
        transform: { type, ...(type === 'concat' ? { separator: existing.transform?.separator ?? ' ' } : {}) },
        sourceColumns: [first, ''],
      });
    } else if (!isMultiSource && wasMultiSource) {
      // Switching AWAY from a multi-source transform → collapse to a single column
      upsertMapping(targetField, {
        transform: { type },
        sourceColumns: [existing?.sourceColumns?.[0] ?? ''],
      });
    } else {
      upsertMapping(targetField, { transform: { type } });
    }
  };

  const setConcatSeparator = (targetField: string, separator: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing || existing.transform?.type !== 'concat') return;
    upsertMapping(targetField, { transform: { type: 'concat', separator } });
  };

  const setConcatColumnAt = (targetField: string, index: number, sourceCol: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    const cols = [...(existing.sourceColumns ?? [])];
    cols[index] = sourceCol;
    upsertMapping(targetField, { sourceColumns: cols });
  };

  const addConcatColumn = (targetField: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    upsertMapping(targetField, { sourceColumns: [...(existing.sourceColumns ?? []), ''] });
  };

  const removeConcatColumn = (targetField: string, index: number) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    const cols = (existing.sourceColumns ?? []).filter((_, i) => i !== index);
    // Concat needs at least 2 columns — if we'd drop below, collapse back to 'none'.
    if (cols.length < 2) {
      upsertMapping(targetField, {
        sourceColumns: cols.length ? cols : [''],
        transform: { type: 'none' },
      });
    } else {
      upsertMapping(targetField, { sourceColumns: cols });
    }
  };

  /** Merge a patch into a mapping's existing transform config. */
  const setTransformConfig = (targetField: string, patch: Partial<FieldTransform>) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    upsertMapping(targetField, {
      transform: { ...(existing.transform ?? { type: 'none' }), ...patch },
    });
  };

  /** Set the lookup entry at `index` (index === entry count appends a new row). */
  const setLookupEntry = (targetField: string, index: number, from: string, to: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    const entries = Object.entries(existing.transform?.lookupMap ?? {});
    entries[index] = [from, to];
    setTransformConfig(targetField, { lookupMap: Object.fromEntries(entries) });
  };

  const removeLookupEntry = (targetField: string, index: number) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    const entries = Object.entries(existing.transform?.lookupMap ?? {});
    entries.splice(index, 1);
    setTransformConfig(targetField, { lookupMap: Object.fromEntries(entries) });
  };

  const setMatchKey = (targetField: string, isMatchKey: boolean) => {
    upsertMapping(targetField, { isMatchKey });
  };

  // ---- AI transform config helpers ----
  const setAiInstruction = (targetField: string, instruction: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    upsertMapping(targetField, {
      transform: { ...(existing.transform ?? { type: 'none' }), instruction: instruction || undefined },
    });
  };

  const setAiOutputFields = (targetField: string, outputFields: string[]) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    upsertMapping(targetField, {
      transform: { ...(existing.transform ?? { type: 'none' }), outputFields: outputFields.length ? outputFields : undefined },
      aiOutputMappings: outputFields.length ? outputFields.map(() => '') : undefined,
    });
  };

  const setAiOutputMapping = (targetField: string, index: number, mappingPath: string) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    const mappings = [...(existing.aiOutputMappings ?? [])];
    mappings[index] = mappingPath;
    upsertMapping(targetField, { aiOutputMappings: mappings });
  };

  const setAiMaxOutputs = (targetField: string, maxOutputs: number) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    // aiSplit output count is constrained to 1..10.
    const clamped = Math.min(10, Math.max(0, Math.trunc(maxOutputs)));
    upsertMapping(targetField, {
      transform: { ...(existing.transform ?? { type: 'none' }), maxOutputs: clamped > 0 ? clamped : undefined },
      aiOutputMappings: clamped > 0 ? Array(clamped).fill('') : undefined,
    });
  };

  const setAiCategories = (targetField: string, categories: string[]) => {
    const existing = mappingByTarget.get(targetField)?.mapping;
    if (!existing) return;
    upsertMapping(targetField, {
      transform: { ...(existing.transform ?? { type: 'none' }), categories: categories.length ? categories : undefined },
    });
  };

  const setDefaultValue = (targetField: string, defaultValue: string) => {
    upsertMapping(targetField, { defaultValue: defaultValue || undefined });
  };

  const removeMapping = (targetField: string) => {
    onMappingsChange(fieldMappings.filter(m => m.targetField !== targetField));
  };

  const acceptSuggestion = (s: MappingSuggestion) => {
    const resolvedTargetField = resolveTargetFieldValue(s.targetField, dataMode);
    if (!resolvedTargetField) return;
    const cols = suggestionSourceCols(s);
    upsertMapping(resolvedTargetField, {
      sourceColumns: cols,
      transform: {
        type: (s.transform as FieldTransformType) ?? 'none',
        ...(s.concatSeparator ? { separator: s.concatSeparator } : {}),
      },
      confidenceScore: s.confidence,
      mappingSource: getSuggestionMappingSource(s),
    } as Partial<FieldMapping>);
    setDismissed(prev => new Set([...prev, ...cols]));
  };

  const acceptAllHigh = () => {
    const high = pendingSuggestions.filter(s => s.confidence >= 0.8);
    const newDismissed = new Set(dismissed);
    // Apply one at a time so upserts chain properly
    let working = [...fieldMappings];
    for (const s of high) {
      const resolvedTargetField = resolveTargetFieldValue(s.targetField, dataMode);
      if (!resolvedTargetField) continue;
      const cols = suggestionSourceCols(s);
      const idx = working.findIndex(m => m.targetField === resolvedTargetField);
      const fm: FieldMapping = {
        targetField: resolvedTargetField,
        sourceColumns: cols,
        isMatchKey: idx >= 0 ? !!working[idx].isMatchKey : false,
        transform: {
          type: (s.transform as FieldTransformType) ?? 'none',
          ...(s.concatSeparator ? { separator: s.concatSeparator } : {}),
        },
        confidenceScore: s.confidence,
        mappingSource: getSuggestionMappingSource(s),
      };
      if (idx >= 0) working[idx] = { ...working[idx], ...fm };
      else working.push(fm);
      for (const col of cols) newDismissed.add(col);
    }
    onMappingsChange(working);
    setDismissed(newDismissed);
  };

  // ---- Summary stats ----
  // A target is considered "resolved" if it has either a source column mapped
  // OR a constant default value, since both produce a value at import time.
  const mappedCount = fieldMappings.filter(m => m.sourceColumns?.[0] || (m.defaultValue && m.defaultValue.length > 0)).length;
  const constantCount = fieldMappings.filter(m => !m.sourceColumns?.[0] && m.defaultValue && m.defaultValue.length > 0).length;
  const unmappedSourceCols = availableColumns.filter(c => !mappedSourceColumns.has(c));

  // ---- AI transform count for the batch/dry-run notice ----
  const aiTransformCount = fieldMappings.filter(
    m => m.transform?.type && (TRANSFORMS_AI.includes(m.transform.type as FieldTransformType) || m.transform.type === 'aiClassify')
  ).length;

  // AI output target options — scoped to ownership.* in owner-update mode;
  // generic mode with an empty catalog falls back to text inputs.
  const aiTargetOptions = useMemo(
    () => dataMode === 'parcelOwnerUpdate'
      ? catalog.filter(o => o.value.startsWith('ownership.'))
      : catalog,
    [catalog, dataMode],
  );
  const useAiTextTargets = aiTargetOptions.length === 0;

  /** Render one AI output target picker (select when a catalog exists). */
  const renderAiOutputTarget = (
    optValue: string,
    index: number,
    label: string,
    existing: FieldMapping | undefined,
    widthClass: string,
  ) => (
    <div key={index} className="flex items-center gap-1">
      <span className={`text-[10px] text-gray-500 ${widthClass} truncate`} title={label}>{label}:</span>
      {useAiTextTargets ? (
        <input
          type="text"
          disabled={disabled}
          className="flex-1 border border-gray-300 rounded-sm px-1.5 py-0.5 text-[10px] text-gray-800 bg-white"
          placeholder="target.field.path"
          value={existing?.aiOutputMappings?.[index] ?? ''}
          onChange={e => setAiOutputMapping(optValue, index, e.target.value)}
          aria-label={`AI output target for ${label}`}
        />
      ) : (
        <select
          disabled={disabled}
          className="flex-1 border border-gray-300 rounded-sm px-1.5 py-0.5 text-[10px] text-gray-800 bg-white"
          value={existing?.aiOutputMappings?.[index] ?? ''}
          onChange={e => setAiOutputMapping(optValue, index, e.target.value)}
          aria-label={`AI output target for ${label}`}
        >
          <option value="">— target —</option>
          {aiTargetOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label} ({o.value})</option>
          ))}
        </select>
      )}
    </div>
  );

  // ---- Render helpers ----
  const renderRow = (opt: TargetFieldOption) => {
    const existing = mappingByTarget.get(opt.value)?.mapping;
    const sourceCol = existing?.sourceColumns?.[0] ?? '';
    // Set of columns already claimed by THIS row — excluded from "used elsewhere".
    const ownCols = new Set<string>((existing?.sourceColumns ?? []).filter(Boolean));
    const defaultValue = existing?.defaultValue ?? '';
    const transform = existing?.transform?.type ?? 'none';
    const isMatchKey = !!existing?.isMatchKey;
    const hasMapping = !!sourceCol;
    const hasConstant = !sourceCol && defaultValue.length > 0;
    const hasValue = hasMapping || hasConstant;
    // Auto-enable default-value mode for rows that already have a defaultValue.
    const inDefaultMode = defaultValueMode.has(opt.value) || hasConstant;
    const preview = previewRow === opt.value && sourceCol
      ? sampleValuesFor(sampleRows, sourceCol)
      : [];
    const isAiTransform = TRANSFORMS_AI.includes(transform as FieldTransformType) || transform === 'aiClassify';
    const isConcat = transform === 'concat';
    const isMultiSource = isConcat || transform === 'coalesce';
    const concatCols = existing?.sourceColumns ?? [];
    const concatSeparator = existing?.transform?.separator ?? ' ';
    const concatSeparatorOptions = getConcatSeparatorOptions(concatSeparator);
    const lookupEntries = Object.entries(existing?.transform?.lookupMap ?? {});

    return (
      <tr
        key={opt.value}
        className={[
          'border-t border-gray-100',
          hasValue ? 'bg-white' : 'bg-gray-50/40',
        ].join(' ')}
      >
        <td className="px-3 py-2 align-top">
          <div className="text-xs font-medium text-gray-800">{opt.label}</div>
          <div className="text-[10px] font-mono text-gray-400">{opt.value}</div>
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex items-center gap-1">
            {inDefaultMode ? (
              <div className="border rounded-sm px-2 py-1 text-xs w-full max-w-[220px] border-gray-200 bg-gray-100 text-gray-400 italic">
                — using constant value —
              </div>
            ) : (
              <SourceColumnCombobox
                value={sourceCol}
                onChange={next => setSourceColumn(opt.value, next)}
                columns={availableColumns}
                usedColumns={mappedSourceColumns}
                ownColumns={ownCols}
                disabled={disabled}
              />
            )}

            {/* Eye — preview sample values (only when source column is mapped) */}
            {sourceCol && !inDefaultMode && (
              <button
                type="button"
                onClick={() => setPreviewRow(prev => prev === opt.value ? null : opt.value)}
                className="p-1 rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                title="Show sample values from this column"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            )}

            {/* X — clear this entire mapping row */}
            {(hasMapping || hasConstant || existing) && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  removeMapping(opt.value);
                  setDefaultValueMode(prev => {
                    const next = new Set(prev);
                    next.delete(opt.value);
                    return next;
                  });
                }}
                className="p-1 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"
                title="Clear this mapping"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Type (T) — toggle into default-value mode */}
            {!inDefaultMode && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggleDefaultMode(opt.value)}
                className="p-1 rounded-sm text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                title="Set a constant / default value instead of mapping a column"
              >
                <Type className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {preview.length > 0 && !inDefaultMode && (
            <div className="mt-1.5 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1 max-w-[260px]">
              <div className="font-semibold text-gray-700 mb-0.5">Sample values:</div>
              {preview.map((v, i) => (
                <div key={i} className="font-mono truncate" title={v}>
                  {v.length > 60 ? `${v.slice(0, 60)}…` : v}
                </div>
              ))}
            </div>
          )}
          {isMultiSource && !inDefaultMode && (
            <div className="mt-1.5 space-y-1.5 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1 max-w-[260px]">
              {isConcat && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-gray-700">Separator</span>
                  <select
                    disabled={disabled}
                    className="border border-gray-300 rounded-sm px-1.5 py-0.5 text-[10px] text-gray-800 w-[126px] bg-white"
                    value={concatSeparator}
                    onChange={e => setConcatSeparator(opt.value, e.target.value)}
                    title="Text inserted between each source column's value"
                  >
                    {concatSeparatorOptions.map((option) => (
                      <option key={`${opt.value}-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <span className="text-[10px] text-gray-400">Choose a standard separator</span>
                </div>
              )}
              {transform === 'coalesce' && (
                <div className="text-[10px] text-gray-500">
                  First non-empty column wins, in the order listed.
                </div>
              )}
              {concatCols.slice(1).map((col, i) => (
                <div key={`extra-${i}`} className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400 w-4 text-right">+</span>
                  <SourceColumnCombobox
                    value={col}
                    onChange={next => setConcatColumnAt(opt.value, i + 1, next)}
                    columns={availableColumns}
                    usedColumns={mappedSourceColumns}
                    ownColumns={ownCols}
                    disabled={disabled}
                    placeholder="— pick column —"
                    className="w-full max-w-[200px]"
                  />
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => removeConcatColumn(opt.value, i + 1)}
                    className="p-0.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove this column from concat"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                disabled={disabled}
                onClick={() => addConcatColumn(opt.value)}
                className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 hover:text-blue-900"
              >
                <Plus className="h-3 w-3" /> Add column
              </button>
            </div>
          )}
          {hasConstant && (
            <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-[10px] font-medium text-orange-800">
              Constant value
            </div>
          )}
        </td>
        <td className="px-3 py-2 align-top">
          {inDefaultMode ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                autoFocus={!hasConstant}
                disabled={disabled}
                className="border border-orange-300 bg-orange-50 rounded-sm px-2 py-1 text-xs text-gray-900 w-full max-w-[150px] focus:outline-hidden focus:ring-1 focus:ring-orange-400"
                placeholder="e.g. United States"
                value={defaultValue}
                onChange={e => setDefaultValue(opt.value, e.target.value)}
                title="This value is applied to every imported row"
              />
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  setDefaultValue(opt.value, '');
                  toggleDefaultMode(opt.value);
                }}
                className="p-1 rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                title="Back to column mapping + transform"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <>
              <select
                disabled={disabled || !sourceCol}
                className="border border-gray-200 rounded-sm px-2 py-1 text-xs text-gray-700 w-full max-w-[130px]"
                value={transform}
                onChange={e => setTransform(opt.value, e.target.value as FieldTransformType)}
              >
                <optgroup label="Working">
                  {TRANSFORMS_WORKING.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </optgroup>
                <optgroup label="AI-powered">
                  {TRANSFORMS_AI.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </optgroup>
                {/* Legacy aiClassify mappings still render but cannot be newly created. */}
                {transform === 'aiClassify' && (
                  <option value="aiClassify" disabled>aiClassify (legacy)</option>
                )}
              </select>
              {transform === 'sanitize' && (
                <div className="mt-1.5 space-y-0.5 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 max-w-[260px]">
                  <label className="text-[10px] font-medium text-gray-700">Characters to remove</label>
                  <input
                    type="text"
                    disabled={disabled}
                    aria-label="Characters to remove"
                    className="w-full border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                    placeholder={'e.g. -&quot;\''}
                    value={existing?.transform?.chars ?? ''}
                    onChange={e => upsertMapping(opt.value, {
                      transform: { ...(existing?.transform ?? { type: 'sanitize' }), type: 'sanitize', chars: e.target.value },
                    })}
                  />
                  <p className="text-[10px] text-gray-400">
                    A literal list of characters to strip — not a pattern. Control characters are always removed.
                  </p>
                </div>
              )}
              {transform === 'split' && (
                <div className="mt-1.5 space-y-0.5 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 max-w-[260px]">
                  <label className="text-[10px] font-medium text-gray-700">Split on separator</label>
                  <input
                    type="text"
                    disabled={disabled}
                    aria-label="Split separator"
                    className="w-full border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                    placeholder=","
                    value={existing?.transform?.separator ?? ''}
                    onChange={e => setTransformConfig(opt.value, { separator: e.target.value })}
                  />
                  <label className="text-[10px] font-medium text-gray-700">Keep part # (0 = first part)</label>
                  <input
                    type="number"
                    min={0}
                    disabled={disabled}
                    aria-label="Split part index"
                    className="w-20 border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                    value={existing?.transform?.splitIndex ?? 0}
                    onChange={e => setTransformConfig(opt.value, { splitIndex: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  />
                </div>
              )}
              {(transform === 'prefix' || transform === 'suffix') && (
                <div className="mt-1.5 space-y-0.5 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 max-w-[260px]">
                  <label className="text-[10px] font-medium text-gray-700">
                    {transform === 'prefix' ? 'Text to prepend' : 'Text to append'}
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    aria-label={transform === 'prefix' ? 'Prefix text' : 'Suffix text'}
                    className="w-full border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                    placeholder={transform === 'prefix' ? 'e.g. USA-' : 'e.g. -CA'}
                    value={existing?.transform?.staticValue ?? ''}
                    onChange={e => setTransformConfig(opt.value, { staticValue: e.target.value })}
                  />
                </div>
              )}
              {transform === 'lookup' && (
                <div className="mt-1.5 space-y-1 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 max-w-[260px]">
                  <label className="text-[10px] font-medium text-gray-700">Value mapping (source → imported)</label>
                  {lookupEntries.map(([from, to], i) => (
                    <div key={i} className="flex items-center gap-1">
                      <input
                        type="text"
                        disabled={disabled}
                        aria-label={`Lookup source value ${i + 1}`}
                        className="w-[45%] border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                        placeholder="R"
                        value={from}
                        onChange={e => setLookupEntry(opt.value, i, e.target.value, to)}
                      />
                      <span className="text-gray-400">→</span>
                      <input
                        type="text"
                        disabled={disabled}
                        aria-label={`Lookup imported value ${i + 1}`}
                        className="flex-1 border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                        placeholder="residential"
                        value={to}
                        onChange={e => setLookupEntry(opt.value, i, from, e.target.value)}
                      />
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => removeLookupEntry(opt.value, i)}
                        className="p-0.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"
                        title="Remove this mapping"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setLookupEntry(opt.value, lookupEntries.length, '', '')}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 hover:text-blue-900"
                  >
                    <Plus className="h-3 w-3" /> Add mapping
                  </button>
                  <p className="text-[10px] text-gray-400">
                    Values not listed here pass through unchanged.
                  </p>
                </div>
              )}
              {isAiTransform && (
                <div className="mt-1.5 space-y-1.5 text-[10px] text-gray-600 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5 max-w-[260px]">
                  <div className="text-[10px] text-blue-700 font-medium">AI Transform Config</div>
                  {/* Instruction (all AI transforms) */}
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-medium text-gray-700">Instruction</label>
                    <textarea
                      disabled={disabled}
                      className="w-full border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white resize-none"
                      rows={2}
                      placeholder="Split into first/last name; trust or company names go to entity name"
                      value={existing?.transform?.instruction ?? ''}
                      onChange={e => setAiInstruction(opt.value, e.target.value)}
                    />
                  </div>
                  {/* aiParse: outputFields */}
                  {transform === 'aiParse' && (
                    <div className="space-y-0.5">
                      <label className="text-[10px] font-medium text-gray-700">Output Fields (comma-separated)</label>
                      <input
                        type="text"
                        disabled={disabled}
                        className="w-full border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                        placeholder="e.g. street, city, state, zip"
                        value={(existing?.transform?.outputFields ?? []).join(', ')}
                        onChange={e => setAiOutputFields(opt.value, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      />
                      {(existing?.transform?.outputFields ?? []).length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          <label className="text-[10px] font-medium text-gray-700">Map outputs to target fields</label>
                          {(existing?.transform?.outputFields ?? []).map((field, i) =>
                            renderAiOutputTarget(opt.value, i, field, existing, 'w-16'),
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* aiSplit: maxOutputs */}
                  {transform === 'aiSplit' && (
                    <div className="space-y-0.5">
                      <label className="text-[10px] font-medium text-gray-700">Max Outputs</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        disabled={disabled}
                        className="w-20 border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                        placeholder="e.g. 4"
                        value={existing?.transform?.maxOutputs ?? ''}
                        onChange={e => setAiMaxOutputs(opt.value, parseInt(e.target.value, 10) || 0)}
                      />
                      {(existing?.transform?.maxOutputs ?? 0) > 0 && (
                        <div className="mt-1 space-y-0.5">
                          <label className="text-[10px] font-medium text-gray-700">Map outputs to target fields</label>
                          {Array.from({ length: existing?.transform?.maxOutputs ?? 0 }).map((_, i) =>
                            renderAiOutputTarget(opt.value, i, `[${i}]`, existing, 'w-8'),
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* aiClassify: categories */}
                  {transform === 'aiClassify' && (
                    <div className="space-y-0.5">
                      <label className="text-[10px] font-medium text-gray-700">Categories (comma-separated)</label>
                      <input
                        type="text"
                        disabled={disabled}
                        className="w-full border border-gray-300 rounded-sm px-1.5 py-1 text-[10px] text-gray-800 bg-white"
                        placeholder="e.g. name, address"
                        value={(existing?.transform?.categories ?? []).join(', ')}
                        onChange={e => setAiCategories(opt.value, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      />
                    </div>
                  )}
                  <div className="text-[9px] text-blue-600 bg-blue-100 rounded-sm px-1.5 py-0.5">
                    AI transforms run in batches and are previewed during dry run.
                  </div>
                  <div className="text-[9px] text-gray-500">
                    AI only fills empty targets; a non-empty deterministic transform result takes precedence.
                  </div>
                </div>
              )}
            </>
          )}
        </td>
        <td className="px-3 py-2 text-center align-top">
          <input
            type="checkbox"
            disabled={disabled || !sourceCol}
            className="w-4 h-4"
            checked={isMatchKey}
            onChange={e => setMatchKey(opt.value, e.target.checked)}
            title={sourceCol ? 'Use this field for upsert match' : 'Map a source column first'}
          />
        </td>
        <td className="px-3 py-2 text-center align-top">
          {existing && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => removeMapping(opt.value)}
              className="p-1 rounded-sm text-gray-400 hover:text-red-600"
              title="Clear this mapping"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-3">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          {errors.map((error) => (
            <p key={error} className="text-xs text-red-700">{error}</p>
          ))}
        </div>
      )}

      {/* AI transform batching notice */}
      {aiTransformCount > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <div className="shrink-0 mt-0.5">
              <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-blue-800">
                {aiTransformCount} AI-powered transform{aiTransformCount > 1 ? 's' : ''} configured
              </p>
              <p className="text-[10px] text-blue-700 mt-0.5">
                AI transforms run in batches and are previewed during dry run.
                {' '}AI only fills empty targets; a non-empty deterministic transform result takes precedence.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mapping summary panel — stats, AI-suggest, sample data, ignored columns */}
      <MappingSummaryPanel
        mappedCount={mappedCount}
        totalTargetFields={catalog.length}
        availableColumns={availableColumns}
        unmappedSourceColumns={unmappedSourceCols}
        sampleRows={sampleRows}
        pendingSuggestions={pendingSuggestions}
        suggestLoading={suggestLoading}
        suggestError={suggestError}
        onSuggest={suggestMappings ? handleAutoSuggest : undefined}
        onAcceptSuggestion={acceptSuggestion}
        onDismissSuggestion={handleDismissSuggestion}
        onAcceptAllHigh={acceptAllHigh}
        disabled={disabled}
        dataMode={dataMode}
        groupInterpretations={groupInterpretations}
        groupInterpretLoading={groupInterpretLoading}
        groupInterpretError={groupInterpretError}
        onInterpretGroups={interpretColumnGroups ? handleInterpretGroups : undefined}
        onAcceptGroupInterpretation={handleAcceptGroupInterpretation}
        onDismissGroupInterpretation={handleDismissGroupInterpretation}
      />

      {/* Main grouped catalog table — scrolls internally with sticky header */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 420px)', minHeight: '320px' }}>
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-xs">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold text-gray-700 w-[32%]">Target Field</th>
              <th className="px-3 py-2 text-xs font-semibold text-gray-700 w-[32%]">Source Column</th>
              <th className="px-3 py-2 text-xs font-semibold text-gray-700 w-[18%]">Transform</th>
              <th className="px-3 py-2 text-xs font-semibold text-gray-700 w-[10%] text-center">
                <div className="inline-flex items-center gap-1">
                  <Key className="h-3 w-3" /> Match
                </div>
              </th>
              <th className="px-3 py-2 text-xs font-semibold text-gray-700 w-[8%] text-center"></th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groups).map(groupName => {
              const opts = groups[groupName];
              const isCollapsed = collapsedGroups.has(groupName);
              const groupMapped = opts.filter(o => !!mappingByTarget.get(o.value)?.mapping?.sourceColumns?.[0]).length;
              return (
                <Fragment key={`group-${groupName}`}>
                  <tr className="bg-gray-100/80 border-t border-gray-200">
                    <td colSpan={5} className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => toggleGroup(groupName)}
                        className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 uppercase tracking-wide hover:text-gray-900"
                      >
                        {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {groupName}
                        <span className="text-gray-400 font-normal normal-case tracking-normal">
                          ({groupMapped}/{opts.length} mapped)
                        </span>
                      </button>
                    </td>
                  </tr>
                  {!isCollapsed && opts.map(renderRow)}
                </Fragment>
              );
            })}
            {extraMappings.length > 0 && (
              <Fragment>
                <tr className="bg-gray-100/80 border-t border-gray-200">
                  <td colSpan={5} className="px-3 py-1.5 text-[11px] font-semibold text-gray-700 uppercase tracking-wide">
                    Custom / Other ({extraMappings.length})
                  </td>
                </tr>
                {extraMappings.map(m => (
                  <tr key={`extra-${m.targetField}`} className="border-t border-gray-100 bg-white">
                    <td className="px-3 py-2 align-top">
                      <div className="text-xs font-medium text-gray-800 font-mono">{m.targetField}</div>
                      <div className="text-[10px] text-gray-400">custom</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <select
                        disabled={disabled}
                        className="border border-gray-300 bg-white rounded-sm px-2 py-1 text-xs text-gray-800 w-full max-w-[220px]"
                        value={m.sourceColumns?.[0] ?? ''}
                        onChange={e => setSourceColumn(m.targetField, e.target.value)}
                      >
                        <option value="">— unmapped —</option>
                        {availableColumns.map(col => <option key={col} value={col}>{col}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <select
                        disabled={disabled || !m.sourceColumns?.[0]}
                        className="border border-gray-200 rounded-sm px-2 py-1 text-xs text-gray-700 w-full max-w-[130px]"
                        value={m.transform?.type ?? 'none'}
                        onChange={e => setTransform(m.targetField, e.target.value as FieldTransformType)}
                      >
                        <optgroup label="Working">
                          {TRANSFORMS_WORKING.map(t => <option key={t} value={t}>{t}</option>)}
                        </optgroup>
                        <optgroup label="AI-powered">
                          {TRANSFORMS_AI.map(t => <option key={t} value={t}>{t}</option>)}
                        </optgroup>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center align-top">
                      <input
                        type="checkbox"
                        disabled={disabled || !m.sourceColumns?.[0]}
                        className="w-4 h-4"
                        checked={!!m.isMatchKey}
                        onChange={e => setMatchKey(m.targetField, e.target.checked)}
                      />
                    </td>
                    <td className="px-3 py-2 text-center align-top">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => removeMapping(m.targetField)}
                        className="p-1 rounded-sm text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </Fragment>
            )}
          </tbody>
          </table>
        </div>
      </div>

      {/* Add custom target field */}
      <AddCustomTargetRow
        disabled={disabled}
        existingTargets={new Set(fieldMappings.map(m => m.targetField))}
        onAdd={(targetField) => {
          onMappingsChange([
            ...fieldMappings,
            { targetField, sourceColumns: [''], isMatchKey: false, transform: { type: 'none' } },
          ]);
        }}
      />

      {/* Ignored source columns are now shown in the Mapping Tools panel above (Ignored Columns tab) */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponent: add a custom target-field path not in the catalog
// ---------------------------------------------------------------------------

function AddCustomTargetRow({
  disabled,
  existingTargets,
  onAdd,
}: {
  disabled: boolean;
  existingTargets: Set<string>;
  onAdd: (targetField: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) { setError('Target field is required.'); return; }
    if (existingTargets.has(trimmed)) { setError('This target field is already mapped.'); return; }
    onAdd(trimmed);
    setValue('');
    setError(null);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
      >
        <Plus className="h-3.5 w-3.5" />
        Add custom target field
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
      <input
        type="text"
        autoFocus
        className="flex-1 border border-gray-300 rounded-sm px-2 py-1 text-xs text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500 font-mono"
        placeholder="e.g. parcelDetails.customField or vendorData.foo"
        value={value}
        onChange={e => { setValue(e.target.value); setError(null); }}
        onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setOpen(false); }}
      />
      <button
        type="button"
        onClick={submit}
        className="px-3 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-sm transition-colors"
      >
        Add
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setError(null); setValue(''); }}
        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-sm"
      >
        Cancel
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
